/**
 * Module dependencies.
 */

import { Trade } from 'app/hooks/requests/apps-crypto/use-get-trades';
import React, { useMemo } from 'react';
import { StatusWrapper } from './trade-item.style';
import BigNumber from 'bignumber.js';
import type { ComponentPropsWithoutRef } from 'react';
import { cn } from 'app/lib/utils';
import { formatNumber } from '../../transactions/form-config';

/**
 * `Props` type.
 */

type Props = {
  item: Trade;
};

/**
 * `Badge` component.
 */

function Badge({ className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('flex w-full flex-col items-center rounded border border-border bg-card p-2', className)}
      {...rest}
    />
  );
}

/**
 * `Title` component.
 */

function Title({ className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('font-bold text-foreground', className)} {...rest} />;
}

/**
 * `Value` component.
 */

function Value({ className, ...rest }: ComponentPropsWithoutRef<'div'>) {
  return <div className={className} {...rest} />;
}

/**
 * Export `Status` component.
 */

export function Status({ item }: Props): JSX.Element {
  const { investment, profit, profitPerc, actualTotal } = useMemo(() => {
    let investment = new BigNumber('0');
    let actualTotal = new BigNumber('0');

    for (const obj of item.transactions) {
      investment = investment.plus(obj.total);
      actualTotal = actualTotal.plus(obj.actualTotal);
    }

    const profitPerc = actualTotal.dividedBy(investment).minus(1).multipliedBy(100).toFixed(2);

    return {
      investment: investment.toString(),
      actualTotal: actualTotal.toString(),
      profitPerc,
      profit: actualTotal.minus(investment).toString()
    };
  }, [item.transactions]);

  return (
    <StatusWrapper>
      <Badge>
        <Title>{'Investment'}</Title>

        <Value>{formatNumber(investment, '$')}</Value>
      </Badge>

      <Badge>
        <Title>{'Actual'}</Title>

        <Value>{formatNumber(actualTotal, '$')}</Value>
      </Badge>

      <Badge>
        <Title>{'Profit'}</Title>

        <Value>{formatNumber(profit, '$')}</Value>
      </Badge>

      <Badge>
        <Title>{'Profit %'}</Title>

        <Value>{profitPerc + '%'}</Value>
      </Badge>
    </StatusWrapper>
  );
}
